import { computed, signal } from '@preact/signals';
import {
  NEW_ROOM,
  normalizeCode,
  type ClientMessage,
  type ClientView,
  type ErrorCode,
  type PublicConfig,
  type PublicPlayer,
  type ReactionId,
  type RequestName,
  type Requests,
  type RoomSettings,
  type RumorDraft,
  type ServerMessage,
  type Session,
  type Vote,
} from '@gazette/shared';
import { locale } from '../i18n/index.ts';

export type ClientError = ErrorCode | 'NETWORK' | 'KICKED';
type Result<T> = { ok: true; data: T } | { ok: false; error: Exclude<ClientError, 'KICKED'> };

const SESSION_KEY = 'gazette.session';
const REPLY_TIMEOUT_MS = 8_000;
const RETRY_MIN_MS = 500;
const RETRY_MAX_MS = 5_000;

export const view = signal<ClientView | null>(null);
export const connection = signal<'connecting' | 'online' | 'offline'>('online');
export const publicConfig = signal<PublicConfig | null>(null);
/** Last error worth telling the player about, shown by the screen that caused it. */
export const lastError = signal<ClientError | null>(null);
/** Server clock minus local clock, so every device counts down to the same instant. */
export const clockOffset = signal(0);

export const me = computed<PublicPlayer | null>(() => {
  const current = view.value;
  return current?.players.find((player) => player.id === current.meId) ?? null;
});
export const isHost = computed(() => me.value?.isHost ?? false);
export const host = computed(() => view.value?.players.find((player) => player.isHost) ?? null);

export function playerById(id: string | null | undefined): PublicPlayer | undefined {
  return view.value?.players.find((player) => player.id === id);
}

export function serverNow(): number {
  return Date.now() + clockOffset.value;
}

function loadSession(): Session | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}

function saveSession(session: Session): void {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  const url = new URL(location.href);
  url.searchParams.set('salon', session.code);
  history.replaceState(null, '', url);
}

function clearSession(): void {
  sessionStorage.removeItem(SESSION_KEY);
}

export const hasSession = signal(loadSession() !== null);

// ------------------------------------------------------------------ socket

/** Every room is its own endpoint, so the socket follows the player from room to room. */
let socket: WebSocket | null = null;
let socketRoom: string | null = null;
let retryDelay = RETRY_MIN_MS;
let retryTimer: number | undefined;
let nextId = 1;
const pending = new Map<number, (result: Result<unknown>) => void>();
const waitingForOpen = new Set<() => void>();

function socketUrl(room: string): string {
  const url = new URL(`/api/rooms/${room}/ws`, location.href);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return url.href;
}

function open(room: string): void {
  if (socket && socketRoom === room && socket.readyState <= WebSocket.OPEN) return;
  close();
  socketRoom = room;
  connection.value = 'connecting';
  const ws = new WebSocket(socketUrl(room));
  socket = ws;

  ws.addEventListener('open', () => {
    if (socket !== ws) return;
    connection.value = 'online';
    retryDelay = RETRY_MIN_MS;
    for (const resolve of waitingForOpen) resolve();
    waitingForOpen.clear();
    const session = loadSession();
    if (session && session.code === room) void resume(session);
  });

  ws.addEventListener('message', (event) => {
    if (socket !== ws || typeof event.data !== 'string') return;
    receive(JSON.parse(event.data) as ServerMessage);
  });

  ws.addEventListener('close', () => {
    if (socket !== ws) return;
    socket = null;
    failPending();
    // Only a seat is worth coming back for; a failed create or join just ends here.
    const session = loadSession();
    if (session && session.code === socketRoom) {
      connection.value = 'offline';
      retryTimer = window.setTimeout(() => open(session.code), retryDelay);
      retryDelay = Math.min(RETRY_MAX_MS, retryDelay * 2);
    } else {
      socketRoom = null;
      connection.value = 'online';
    }
  });
}

function close(): void {
  window.clearTimeout(retryTimer);
  const ws = socket;
  socket = null;
  socketRoom = null;
  failPending();
  connection.value = 'online';
  ws?.close(1000);
}

function failPending(): void {
  for (const resolve of pending.values()) resolve({ ok: false, error: 'NETWORK' });
  pending.clear();
  for (const resolve of waitingForOpen) resolve();
  waitingForOpen.clear();
}

function receive(message: ServerMessage): void {
  switch (message.type) {
    case 'reply': {
      const resolve = pending.get(message.id);
      pending.delete(message.id);
      resolve?.(message.result);
      break;
    }
    case 'state':
      clockOffset.value = message.view.serverNow - Date.now();
      view.value = message.view;
      break;
    case 'kicked':
      forget();
      lastError.value = 'KICKED';
      break;
  }
}

function whenOpen(): Promise<void> {
  if (socket?.readyState === WebSocket.OPEN) return Promise.resolve();
  return new Promise((resolve) => waitingForOpen.add(resolve));
}

async function request<K extends RequestName>(type: K, payload: Requests[K]['payload']): Promise<Result<Requests[K]['result']>> {
  const timeout = new Promise<Result<never>>((resolve) => window.setTimeout(() => resolve({ ok: false, error: 'NETWORK' }), REPLY_TIMEOUT_MS));
  const reply = (async (): Promise<Result<Requests[K]['result']>> => {
    await whenOpen();
    const ws = socket;
    if (ws?.readyState !== WebSocket.OPEN) return { ok: false, error: 'NETWORK' };
    const id = nextId++;
    const done = new Promise<Result<unknown>>((resolve) => pending.set(id, resolve));
    ws.send(JSON.stringify({ id, type, payload } as ClientMessage));
    return (await done) as Result<Requests[K]['result']>;
  })();
  return Promise.race([reply, timeout]);
}

async function run<K extends RequestName>(type: K, payload: Requests[K]['payload']): Promise<Requests[K]['result'] | null> {
  const result = await request(type, payload);
  if (result.ok) {
    lastError.value = null;
    return result.data;
  }
  lastError.value = result.error;
  if (result.error === 'SESSION_INVALID') forget();
  return null;
}

function forget(): void {
  clearSession();
  hasSession.value = false;
  view.value = null;
  close();
}

async function resume(session: Session): Promise<void> {
  const result = await request('room:resume', session);
  if (!result.ok && result.error !== 'NETWORK') {
    forget();
    if (result.error !== 'ROOM_NOT_FOUND') lastError.value = result.error;
  }
}

async function enter(room: string, type: 'room:create' | 'room:join', payload: Requests['room:create' | 'room:join']['payload']): Promise<boolean> {
  clearSession();
  open(room);
  const session = (await run(type, payload as never)) as Session | null;
  if (!session) {
    close();
    return false;
  }
  saveSession(session);
  hasSession.value = true;
  return true;
}

/** Called once at startup: a stored seat reconnects on its own. */
export function restoreSession(): void {
  const session = loadSession();
  if (session) open(session.code);
}

export async function loadPublicConfig(): Promise<void> {
  try {
    const response = await fetch('/api/config');
    if (response.ok) publicConfig.value = (await response.json()) as PublicConfig;
  } catch {
    // The lobby falls back to the page's own address.
  }
}

export function joinUrl(code: string): string {
  const base = publicConfig.value?.publicUrl ?? location.origin;
  return `${base}/?salon=${code}`;
}

export const actions = {
  create: (name: string) => enter(NEW_ROOM, 'room:create', { name, locale: locale.value }),
  async join(rawCode: string, name: string) {
    const code = normalizeCode(rawCode);
    if (!code) {
      lastError.value = 'ROOM_NOT_FOUND';
      return false;
    }
    return enter(code, 'room:join', { name });
  },
  async leave() {
    await request('room:leave', null);
    forget();
    const url = new URL(location.href);
    url.searchParams.delete('salon');
    history.replaceState(null, '', url);
  },
  settings: (patch: Partial<RoomSettings>) => run('lobby:settings', patch),
  rerollPortrait: () => run('lobby:portrait', null),
  kick: (playerId: string) => run('lobby:kick', { playerId }),
  addBot: () => run('lobby:add-bot', null),
  start: () => run('game:start', null),
  confessions: (answers: string[], done: boolean) => run('confessions:update', { answers, done }),
  writing: (drafts: RumorDraft[], done: boolean) => run('writing:update', { drafts, done }),
  vote: (articleId: string, patch: Partial<Vote>) => run('investigation:vote', { articleId, ...patch }),
  react: (articleId: string, reaction: ReactionId) => run('investigation:react', { articleId, reaction }),
  plume: (articleId: string) => run('plume:vote', { articleId }),
  advance: () => run('host:advance', null),
  restart: () => run('host:restart', null),
};
